<template>
  <div ref="popoverBase">
    <div ref="trigger" data-cy="trigger" @click="handleOpen">
      <slot name="trigger" />
    </div>
    <div ref="popover" class="z-50">
      <template v-if="showContent">
        <slot name="content" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, type PropType, watch } from "vue";
import {
  createPopper,
  type VariationPlacement,
  type BasePlacement,
  type Instance,
} from "@popperjs/core";
import { onClickOutside } from "@vueuse/core";

const emit = defineEmits<{
  "update:open": [val: boolean];
}>();

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  position: {
    type: String as PropType<VariationPlacement | BasePlacement>,
    default: "bottom-start",
  },
  offset: {
    type: Array as PropType<number[]>,
    default: () => [0, 0],
  },
});

const popoverBase = ref<HTMLDivElement | null>(null);
const trigger = ref<HTMLDivElement | null>(null);
const popover = ref<HTMLDivElement | null>(null);

const popper = ref<Instance | null>(null);

const showContent = computed({
  get() {
    return props.open;
  },
  set(value) {
    emit("update:open", value);
  },
});

onClickOutside(popoverBase, () => {
  if (showContent.value) {
    showContent.value = false;
  }
});

const handleOpen = () => {
  showContent.value = !showContent.value;
  popper.value?.update();
};

watch(
  () => props.offset,
  (newVal) => {
    popper.value?.setOptions({
      modifiers: [
        {
          name: "offset",
          options: {
            offset: newVal,
          },
        },
      ],
    });
    popper.value?.update();
  }
);

onMounted(() => {
  if (!trigger.value || !popover.value) {
    return;
  }
  popper.value = createPopper(trigger.value, popover.value, {
    placement: props.position,
    modifiers: [
      {
        name: "offset",
        options: {
          offset: props.offset,
        },
      },
    ],
  });
});
</script>
